"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { InlineRichText } from "@/components/atoms/RichText";
import type { PortableTextBlock } from "@portabletext/types";
import { SECTION_CONTAINER_WIDTHS, type SectionContainerWidth } from "@/components/atoms/section-constants";
import { SectionBackground } from "../modules/Section/SectionBackground";
import { SectionContent } from "../modules/Section/SectionContent";
import { BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { isDarkColor } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { getPaddingClass } from "@/lib/spacing";
import { PortableKeyPoints as PortableKeyPointsType, KeyPoints as KeyPointsType } from "@/content/types";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
interface PortableKeyPointsProps {
  value: PortableKeyPointsType;
}
type KeyPointsModuleProps = KeyPointsType & {
  _key: string;
  isDark?: boolean;
};

/**
 * Shared KeyPoints renderer component
 *
 * Split layout: 45% left (heading + highlight bar + body), 55% right (glass box with points)
 * Glass box breaks out to the right edge of the viewport
 * Mobile: Stacked
 */
const KeyPointsContent = t0 => {
  const $ = _c(17);
  const {
    data,
    isDark: t1
  } = t0;
  const isDark = t1 === undefined ? false : t1;
  const {
    heading,
    body,
    points
  } = data;
  if (!heading || !points || points.length === 0) {
    return null;
  }
  let t2;
  if ($[0] !== heading) {
    t2 = <Heading level={2} className="text-3xl md:text-4xl font-bold mb-6">{heading}</Heading>;
    $[0] = heading;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Box className="w-36 h-2 bg-primary mb-6" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== body || $[4] !== isDark) {
    t4 = body && body.length > 0 && <Box className={cn("prose prose-lg max-w-none", isDark && "prose-invert")}><InlineRichText content={body as PortableTextBlock[]} /></Box>;
    $[3] = body;
    $[4] = isDark;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <Box className="lg:col-span-5 lg:pr-12 flex flex-col justify-center">{t2}{t3}{t4}</Box>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Box className={cn("absolute inset-y-0 left-0", "rounded-3xl lg:rounded-l-[var(--radius-glass)] lg:rounded-r-none", "backdrop-blur-sm bg-overlay", "shadow-2xl", "lg:w-[var(--aq-bleed-total)] lg:-mr-[var(--aq-bleed-offset)]", "w-full h-full lg:h-auto")} />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== points) {
    t7 = points.map(_temp);
    $[10] = points;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <Box className="lg:col-span-7 relative group">{t6}<Box className="relative z-10 p-8 md:p-10 lg:pl-16 lg:py-12 flex items-center min-h-full"><Box className="grid gap-6 w-full">{t7}</Box></Box></Box>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t5 || $[15] !== t8) {
    t9 = <Box className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-stretch">{t5}{t8}</Box>;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};

/**
 * PortableKeyPoints Component
 *
 * For use within Portable Text (blockContent)
 */
const PortableKeyPoints = t0 => {
  const $ = _c(36);
  const {
    value
  } = t0;
  const {
    style
  } = value;
  const bgColor = style?.backgroundColor || "white";
  const paddingTop = style?.paddingTop || "pt-16";
  const paddingBottom = style?.paddingBottom || "pb-16";
  const containerWidthValue = style?.containerWidth || "max-w-7xl";
  const customClasses = style?.customClasses || "";
  let T0;
  let T1;
  let containerSize;
  let isDark;
  let t1;
  let t2;
  let t3;
  if ($[0] !== bgColor || $[1] !== containerWidthValue || $[2] !== customClasses || $[3] !== paddingBottom || $[4] !== paddingTop || $[5] !== value.heading) {
    const ptClass = getPaddingClass(paddingTop, "pt-16");
    const pbClass = getPaddingClass(paddingBottom, "pb-16");
    containerSize = (containerWidthValue in SECTION_CONTAINER_WIDTHS ? containerWidthValue : "max-w-7xl") as SectionContainerWidth;
    let t4;
    if ($[13] !== bgColor) {
      t4 = isDarkColor(bgColor);
      $[13] = bgColor;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    isDark = t4;
    T1 = Box;
    t3 = "not-prose my-12";
    T0 = SectionBackground;
    t1 = value.heading || "Key points";
    t2 = cn(ptClass, pbClass, "relative transition-colors duration-200 ease-in-out", customClasses);
    $[0] = bgColor;
    $[1] = containerWidthValue;
    $[2] = customClasses;
    $[3] = paddingBottom;
    $[4] = paddingTop;
    $[5] = value.heading;
    $[6] = T0;
    $[7] = T1;
    $[8] = containerSize;
    $[9] = isDark;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    T0 = $[6];
    T1 = $[7];
    containerSize = $[8];
    isDark = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
  }
  const t4 = style?.backgroundImage;
  const t5 = style?.backgroundVideo;
  const t6 = style?.backgroundPattern as BackgroundPatternType;
  const t7 = style?.patternOpacity;
  let t8;
  if ($[15] !== isDark || $[16] !== value) {
    t8 = <KeyPointsContent data={value} isDark={isDark} />;
    $[15] = isDark;
    $[16] = value;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== containerSize || $[19] !== t8) {
    t9 = <SectionContent size={containerSize} className="h-full">{t8}</SectionContent>;
    $[18] = containerSize;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== T0 || $[22] !== bgColor || $[23] !== isDark || $[24] !== t1 || $[25] !== t2 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t9) {
    t10 = <T0 aria-label={t1} className={t2} backgroundColor={bgColor} backgroundImage={t4} backgroundVideo={t5} backgroundPattern={t6} patternOpacity={t7} isDark={isDark}>{t9}</T0>;
    $[21] = T0;
    $[22] = bgColor;
    $[23] = isDark;
    $[24] = t1;
    $[25] = t2;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== T1 || $[33] !== t10 || $[34] !== t3) {
    t11 = <T1 className={t3}>{t10}</T1>;
    $[32] = T1;
    $[33] = t10;
    $[34] = t3;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};

/**
 * KeyPointsModule Component
 *
 * For use within Section modules (via ModulesRenderer)
 * Does not wrap with SectionBackground as the parent Section handles that
 */
export const KeyPointsModule = t0 => {
  const $ = _c(6);
  let isDark;
  let props;
  if ($[0] !== t0) {
    ({
      isDark,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = isDark;
    $[2] = props;
  } else {
    isDark = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== isDark || $[4] !== props) {
    t1 = <KeyPointsContent data={props} isDark={isDark} />;
    $[3] = isDark;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export default PortableKeyPoints;
function _temp(point, index) {
  if (!point.content) {
    return null;
  }
  return <Box key={point._key || `point-${index}`} className={cn("prose prose-xl max-w-none", "text-[var(--overlay-foreground)]", "prose-headings:text-[var(--overlay-foreground)]", "prose-p:text-[var(--overlay-foreground)]", "prose-strong:text-[var(--overlay-foreground)]", "prose-li:text-[var(--overlay-foreground)]")}><InlineRichText content={point.content as PortableTextBlock[]} /></Box>;
}